import { floorToMinute, toDateInputValue, toTimeInputValue } from './entryTime';

/**
 * Overlap and "not in the future" rules for moving a timesheet entry. Pure: nothing here
 * reads a clock, a network or the DOM.
 *
 * One time basis for everything: the FLOORED MINUTE. An entry's `at` carries seconds
 * (a timer starts at `now()`), but durations are whole minutes and the row shows minutes,
 * so an interval is `[floor(at), floor(at) + minutes)` and seconds never take part in a
 * comparison. Without it a timer stopped at 10:00:47 and one started at 10:00:47 would
 * look like they overlap, and a "fixed" end would drift by up to a minute.
 *
 * Intervals are half open, so an entry ending at 10:00 and one starting at 10:00 touch
 * without overlapping. They are absolute instants, so an entry that crosses midnight is
 * compared with the next day's entries without any per-day arithmetic.
 *
 * @typedef {{start: number, end: number}} Interval Epoch milliseconds, `start < end`.
 * @typedef {Interval & {entry: object|null, running: boolean}} Occupied An interval that
 *   something already holds: a live entry, or the running timer (`running`, `entry` is the
 *   timer's own payload).
 */

/** Shown when an edit would end after the current minute. */
export const FUTURE_MESSAGE = "An entry can't end in the future.";

/**
 * The floored-minute interval an entry covers.
 *
 * @param {{at?: string, minutes?: number}} entry
 * @returns {Interval|null} `null` when the entry has no usable start or a positive length.
 */
export function intervalOf(entry) {
  const at = new Date(entry?.at).getTime();
  const minutes = Number(entry?.minutes);
  if (!Number.isFinite(at) || !(minutes > 0)) return null;
  const start = floorToMinute(at);
  return { start, end: start + minutes * 60000 };
}

/**
 * The running timers that should hold time, given what the loaded list already knows.
 *
 * The timesheet's own `running` always counts. The shell's timer (`shell`) is the same
 * timer, or one started from the shell, task or thread that the list may not be refetched
 * for. Once the list has caught up with it, counting it would be wrong: a timer stopped
 * elsewhere (another tab, the server's reaper) leaves the shell still holding it, while the
 * list now carries its finished entry `[started_at, stop)`, so it would hold
 * `[started_at, now)` on top of its own entry. The shell timer is therefore dropped when
 * a loaded human entry has its `id` or the same floored start (a stopped timer keeping
 * its id is unconfirmed, so both are matched), or when it is the page's own timer.
 *
 * @param {{running?: object|null, shell?: object|null, entries?: object[]}} args
 * @returns {object[]} Zero, one or two timers, for `occupiedIntervals`.
 */
export function timersHoldingTime({ running = null, shell = null, entries = [] } = {}) {
  const timers = [];
  if (running) timers.push(running);
  if (!shell) return timers;

  const startMinute = floorToMinute(new Date(shell.started_at).getTime());
  const sameTimer = (other) => other
    && ((shell.id != null && other.id === shell.id)
      || (Number.isFinite(startMinute) && floorToMinute(new Date(other.started_at ?? other.at).getTime()) === startMinute));
  const caughtUp = (entries || []).some((e) => e && e.actor_type !== 'agent' && sameTimer(e));
  if (!caughtUp && !sameTimer(running)) timers.push(shell);
  return timers;
}

/**
 * Everything that holds time on the person's timesheet, earliest first.
 *
 * Left out: a superseded original (`corrected: true`; its correction is the live row), an
 * agent entry (a run has no wall-clock window, only a cost), the entry being edited, and
 * anything without a usable interval. A running timer holds `[started_at, now)`; `running`
 * may be one timer or a list (the timesheet's own, and one started from the shell that the
 * timesheet has not been refetched for).
 *
 * @param {object[]} entries fn_my_timesheet entries, any days.
 * @param {{running?: object|object[]|null, now: number, excludeId?: string}} options
 *   `now` is epoch milliseconds.
 * @returns {Occupied[]}
 */
export function occupiedIntervals(entries, { running = null, now, excludeId = null } = {}) {
  const occupied = [];
  for (const entry of entries || []) {
    if (!entry || entry.corrected === true || entry.actor_type === 'agent') continue;
    if (excludeId != null && entry.id === excludeId) continue;
    const interval = intervalOf(entry);
    if (interval) occupied.push({ ...interval, entry, running: false });
  }

  const timers = Array.isArray(running) ? running : [running];
  for (const timer of timers) {
    const startedAt = new Date(timer?.started_at).getTime();
    if (!Number.isFinite(startedAt) || !Number.isFinite(now)) continue;
    const start = floorToMinute(startedAt);
    const end = floorToMinute(now);
    if (end > start) occupied.push({ start, end, entry: timer, running: true });
  }

  return occupied.sort((a, b) => a.start - b.start);
}

/**
 * The part of a new interval the old one did not already cover: the time an edit ADDS.
 *
 * Only added time is tested, so shortening an entry adds nothing and is always allowed,
 * extending one that already overlaps another is rejected only if the extension itself
 * overlaps, and moving it to another day adds the whole interval.
 *
 * @param {Interval|null} oldInterval `null` when there was no usable old interval.
 * @param {Interval} newInterval
 * @returns {Interval[]} Zero, one or two segments (a new interval that spans the old one on
 *   both sides leaves one on each).
 */
export function addedIntervals(oldInterval, newInterval) {
  if (!oldInterval) return [newInterval];
  const added = [];
  const left = { start: newInterval.start, end: Math.min(newInterval.end, oldInterval.start) };
  if (left.end > left.start) added.push(left);
  const right = { start: Math.max(newInterval.start, oldInterval.end), end: newInterval.end };
  if (right.end > right.start) added.push(right);
  return added;
}

/**
 * The first held interval that one of the candidates overlaps.
 *
 * @param {Interval|Interval[]} candidates
 * @param {Occupied[]} occupied As returned by `occupiedIntervals` (sorted, so the earliest
 *   conflict for a candidate wins).
 * @returns {Occupied|null}
 */
export function findOverlap(candidates, occupied) {
  for (const c of Array.isArray(candidates) ? candidates : [candidates]) {
    const hit = (occupied || []).find((o) => c.start < o.end && o.start < c.end);
    if (hit) return hit;
  }
  return null;
}

const shorten = (text, max = 40) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/**
 * The message for an overlap, naming the entry it hit: `Overlaps 'Client call'
 * 10:00–11:30 (Verde Ranch)`. A conflict on another day than `referenceStart`'s carries its
 * date.
 *
 * @param {Occupied} conflict
 * @param {number} referenceStart Epoch ms of the edited entry's new start.
 * @returns {string}
 */
export function describeOverlap(conflict, referenceStart) {
  const start = new Date(conflict.start);
  const end = new Date(conflict.end);
  const sameDay = toDateInputValue(start) === toDateInputValue(new Date(referenceStart));
  const day = sameDay ? '' : `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, `;
  const when = `${day}${toTimeInputValue(start)}–${toTimeInputValue(end)}`;
  const what = shorten(String(conflict.entry?.what ?? conflict.entry?.title ?? '').trim());

  if (conflict.running) {
    return `Overlaps the running timer${what ? ` '${what}'` : ''}, since ${toTimeInputValue(start)}.`;
  }
  const client = conflict.entry?.client ? ` (${conflict.entry.client})` : '';
  return `Overlaps '${what || 'No description'}' ${when}${client}`;
}

/**
 * Checks a move against the person's other time. Null means "allowed".
 *
 * Only the time the edit adds is tested, against both rules: it must not end after the
 * current minute, and it must not overlap anything held. An edit that adds nothing
 * (shortening, or no change) is always allowed, including on an entry that is already in
 * the future or already overlapping.
 *
 * @param {object} args
 * @param {object} args.entry The entry as it is now (`at`, `minutes`, `id`).
 * @param {Date} args.start The new start.
 * @param {number} args.minutes The new duration in whole minutes.
 * @param {object[]} args.entries Every entry that could overlap, across the days involved.
 * @param {object|object[]|null} [args.running] The running timer payload, or several.
 * @param {number} args.now Epoch milliseconds.
 * @returns {{reason:'future'|'overlap', message:string, conflict?:Occupied}|null}
 */
export function checkMove({ entry, start, minutes, entries, running = null, now }) {
  const startMs = floorToMinute(start.getTime());
  const next = { start: startMs, end: startMs + minutes * 60000 };
  const added = addedIntervals(intervalOf(entry), next);
  if (added.length === 0) return null;

  const currentMinute = floorToMinute(now);
  if (added.some((a) => a.end > currentMinute)) return { reason: 'future', message: FUTURE_MESSAGE };

  const conflict = findOverlap(added, occupiedIntervals(entries, { running, now, excludeId: entry?.id }));
  if (conflict) return { reason: 'overlap', message: describeOverlap(conflict, startMs), conflict };
  return null;
}

const addDays = (date, n) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate() + n, 12);
  return d;
};

/**
 * The days that must be loaded to check a move onto `start`'s day, or `null` when the
 * loaded range already covers them.
 *
 * An entry belongs to the day it starts on, so the neighbour that can overlap is the day
 * before (an entry crossing midnight into the target). The day after is fetched as a
 * hedge: an entry starting there cannot overlap one that ends by midnight, but the
 * timesheet's `p_from` / `p_to` may filter on the UTC day (§5f.4 #5 of the backend
 * request), in which case an edge day arrives partial. The day after is capped at today:
 * nothing can be logged beyond it, and without the cap every edit on today's entries would
 * fetch a day that is always empty. The first and last loaded days are not covered for
 * the neighbour outside the range.
 *
 * @param {Date} start The first moment to check (the start of the added time).
 * @param {{from: string, to: string, today: string}} range `YYYY-MM-DD` day keys.
 * @param {Date} [end] The last moment to check, exclusive; defaults to `start`. An interval
 *   that crosses midnight takes the day after the one it ends on, as the same hedge.
 * @returns {{from: string, to: string}|null} The day keys to fetch, inclusive.
 */
export function neighbourWindow(start, { from, to, today }, end = start) {
  const lastMoment = end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : end;
  const before = toDateInputValue(addDays(start, -1));
  let after = toDateInputValue(addDays(lastMoment, 1));
  if (after > today) after = today;
  if (after < before) after = before;
  if (before >= from && after <= to) return null;
  return { from: before, to: after };
}
